import axios, { AxiosHeaders, AxiosInstance } from 'axios';
import { toast } from 'react-toastify';
import { cognitoSignOut, getToken } from '../services/authentication';
import { errorMessagesTranslationMapper } from './serverErrorsMapper';
import { sharedPaths } from '../router/constants/route-paths';
import { ServerErrors } from '../localization/definitions/types';

declare module 'axios' {
    export interface AxiosRequestConfig {
        skipAuth?: boolean;
    }

    export interface InternalAxiosRequestConfig {
        skipAuth?: boolean;
    }
}

const SuSyApi: AxiosInstance = axios.create({
    baseURL: `${process.env.REACT_APP_SUSY_API}/api/v1`,
});

export interface ErrorMessage {
    message: keyof ServerErrors;
    errMsgParams: [];
}

SuSyApi.interceptors.request.use(async function (config) {
    if (config.skipAuth) {
        return config;
    }

    const token = await getToken();
    if (token && config.headers) {
        (config.headers as AxiosHeaders).set('Authorization', token);
    }
    return config;
});

SuSyApi.interceptors.response.use(
    function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        return response;
    },
    async function (error) {
        // Any status codes that falls outside the range of 2xx cause this function to trigger

        if (error?.response?.status === 401) {
            const userDoesntExsistInDatabase =
                error.response &&
                error.response.data.errors.some(
                    (err: ErrorMessage) =>
                        err.message === 'USER_DOESNT_EXIST_MSG'
                );

            const isForcePasswordChange =
                error.response &&
                error.response.data.errors.some(
                    (err: ErrorMessage) => err.message === 'AUTH_EXPIRED' as keyof ServerErrors
                );

            // wait before redirect in order to show error message if user is present in AWS Cognito but not in database
            if (userDoesntExsistInDatabase) {
                // Cannot use cognitoSignOut because backend will remove this user at this point
                localStorage.clear();
                setTimeout(async () => {
                    window.location.href = '/';
                }, 3000);
            } else if (isForcePasswordChange) {
                if (
                    window.location.pathname !==
                    `/${sharedPaths.forcePasswordChange}`
                ) {
                    window.location.pathname = `/${sharedPaths.forcePasswordChange}`;
                }
                return;
            } else {
                await cognitoSignOut();
                window.location.href = '/';
            }
        }

        if (error.response && error.response.data.errors) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx

            const { errors }: { errors: ErrorMessage[] } = error.response.data;
            const errorMessages = errors
                ? errors
                      .map((err) =>
                          errorMessagesTranslationMapper(
                              err.message,
                              err.errMsgParams
                          )
                      )
                      .join(' ')
                : errorMessagesTranslationMapper('DEFAULT_ERROR_MESSAGE_MSG');

            toast.warning(errorMessages);
        } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.log(error.request);
            toast.warning(
                errorMessagesTranslationMapper('SOMETHING_WENT_WRONG_MSG')
            );
        } else {
            // Something happened in setting up the request that triggered an Error
            console.log(
                'Error',
                errorMessagesTranslationMapper(
                    error.message,
                    error.errMsgParams
                )
            );
            toast.warning(
                errorMessagesTranslationMapper('CLIENT_REQUEST_INVALID_MSG')
            );
        }

        // Do something with response error
        return Promise.reject(error);
    }
);

export { SuSyApi };
